/**
 * State for the shared `components/SnackBarAlert` on the Self-Serve pages: API errors and
 * success messages appear there (top-center, auto-hides after 5 seconds). Also holds the
 * page-level messages shown when a public class landing page cannot be loaded.
 */
export interface SsAlertState {
  /** Whether the snackbar is shown. */
  open: boolean;
  /** MUI Alert severity. */
  type: "success" | "error";
  /** Text to show. */
  msg: string;
}

/** Closed snackbar; the initial state. */
export const CLOSED_ALERT: SsAlertState = { open: false, type: "error", msg: "" };

/**
 * Forgot: shown for every HTTP 200, including when no mail was sent.
 * Same text whether or not a code existed. Not read from the response body.
 */
export const FORGOT_ACKNOWLEDGED_MESSAGE = "An email has been sent to your registered address.";

/** Forgot: blank, wrong shape, over 254 characters, or HTTP 400. Not the API sentence. */
export const FORGOT_INVALID_EMAIL_MESSAGE = "Please enter a valid email";

/** Forgot: any failure other than HTTP 400. */
export const FORGOT_SUBMIT_FAILED_MESSAGE = "We could not submit that request. Please try again.";

/** Redeem: an unknown email or an invalid code. One message, so neither one is singled out. */
export const REDEEM_INVALID_MESSAGE = "Invalid email address or access code";

/** Fallback message when the class cannot be loaded. */
export const UNAVAILABLE_MESSAGE = "This class is not available.";

/** Shown for an unknown slug and for a deleted class alike — both resolve to 404. */
export const INVALID_LINK_MESSAGE = "This class landing page was not found";

/** Shown when the class exists but is no longer accepting registrations (HTTP 410). */
export const REGISTRATION_CLOSED_MESSAGE =
  "This class is no longer accepting registrations.";

/**
 * Snackbar text for a failed check, register, or enroll. An unknown email and an invalid code
 * share {@link REDEEM_INVALID_MESSAGE}; every other code keeps the API `userMessage`.
 */
export function redeemErrorMessage(err: any): string {
  switch (err?.errorCode) {
    case "SS_STUDENT_NOT_FOUND":
    case "SS_ACCESS_CODE_INVALID":
      return REDEEM_INVALID_MESSAGE;
    default:
      return err?.userMessage || "We could not redeem that access code.";
  }
}

/**
 * Maps a class-load or enroll error to the page-level message: 404 = invalid link (unknown or deleted),
 * 410 = no longer accepting registrations, anything else = not available.
 */
export const resolveErrorMessage = (err: any): string => {
  const status = err?.response?.status;
  if (!err?.response) {
    return UNAVAILABLE_MESSAGE;
  }
  if (status === 404) {
    return INVALID_LINK_MESSAGE;
  }
  if (status === 410) {
    return REGISTRATION_CLOSED_MESSAGE;
  }
  return UNAVAILABLE_MESSAGE;
};

/** History state the landing page passes to Access Simulation after a successful redeem. */
export interface SsAccessSimulationLocationState {
  /** True right after a redeem, so Access Simulation shows the success message once. */
  redeemed?: boolean;
}
